// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngSticky from "@plumberssupply/App/Components/XngSticky";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC, useMemo, useState } from "react";
import { css } from "styled-components";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    mainSection?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

const cartPageTemplateStyles: CartPageTemplateStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    headerSection: {
        css: css`
            width: calc(100% - 308px);
            padding-right: 22px;
            ${xScreen("lte", 767)} {
                width: 100%;
                padding: 0;
            }
        `,
    },
    actionsSection: {
        css: css`
            width: 100%;
        `,
    },
    mainSection: {
        css: css`
            width: calc(100% - 308px);
            padding-right: 22px;
            
            ${xScreen("lte", "md")} {
                width: calc(100% - 260px);
            }

            ${xScreen("lte", 767)} {
                width: 100%;
                padding: 0;
                order: 3;
            }

            @media print {
                width: 100%;
            }
        `,
    },
    totalSection: {
        css: css`
            width: 308px;
            ${xScreen("lte", "md")} {
                width: 260px;
            }

            ${xScreen("lte", 767)} {
                width: 100%;
                display: flex;
                justify-content: center;
                order: 2;
                margin-bottom: 32px;

                & > div.xng-sticky {
                    max-width: 100%;
                    width: 100%;
                }
            }

            @media print {
                width: 100%;
                page-break-before: always;
            }
        `,
    },
};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    return (
        <CartPageTemplateContext.Provider value={value}>
            <StyledWrapper {...styles.container}>{children}</StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const MainSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

const CartTotalSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection}>
            <XngSticky>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.mainSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
